/* Pocket Factory: the ELI5 page (sections/pf-eli5.liquid, sections/pf-eli5-answers.liquid). Progressive: the page reads
   complete without this file. Four parts, each only where its markup is:
   - copy buttons ([data-pfe-copy]): shown here, they copy their words and say so once in their widget's status line;
   - the hero ripple (.pfe-ripple): its three waves start once it is fully on screen (.is-live); without this file, or
     under reduced motion, only its resting ring shows;
   - the sticky bar ([data-pfe-bar]): shown while the hero's button is off screen (above or below it, or only a sliver
     of it at the screen's edge) and neither the card panel nor the gift is on screen ([data-pfe-bar-hide]); its first
     state at load shows without the slide-in; without IntersectionObserver it stays hidden;
   - the gift ([data-pfe-gift]): a scratch card laid over the face when it nears the screen. Only sideways drags scratch
     (touch-action: pan-y pinch-zoom, so vertical scrolling and pinch-zoom always pass); about 40% cleared, or "Reveal
     it", shows the face. Under reduced motion, without a canvas, or when focus is already on the face, the face simply
     stays shown. The how-to line ([data-pfe-how]) shows only while the card covers the face. After the reveal the
     store's first-order offer line shows only while PocketEmailOffer says the offer is available (asked again when its
     dialog closes and on a back/forward-cache return); this file never writes a code or an amount.
   Only transform and opacity animate. Running it twice binds nothing twice, and the theme editor's section re-renders
   (shopify:section:load / unload) are wired again: new nodes are bound once, the bar's observers are rebuilt. */
(function (root) {
  'use strict';
  const doc = root.document;
  if (!doc || root.PocketEli5) return;
  root.PocketEli5 = { version: 1 };

  const reduce = (() => {
    try { return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (_) { return false; }
  })();
  const hasIO = 'IntersectionObserver' in root;
  // Every match in a scope (the document, or a re-rendered section), the scope itself included.
  const all = (scope, selector) => {
    const found = scope && typeof scope.querySelectorAll === 'function' ? [...scope.querySelectorAll(selector)] : [];
    if (scope && typeof scope.matches === 'function' && scope.matches(selector)) found.unshift(scope);
    return found;
  };

  /* ---------- Copy buttons ---------- */
  async function copyText(text, button) {
    try {
      if (root.navigator && root.navigator.clipboard && typeof root.navigator.clipboard.writeText === 'function') {
        await root.navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) { /* falls through to the selection copy */ }
    let ok = false;
    const area = doc.createElement('textarea');
    try {
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.insetBlockStart = '0';
      area.style.opacity = '0';
      doc.body.appendChild(area);
      area.select();
      ok = typeof doc.execCommand === 'function' && doc.execCommand('copy') === true;
    } catch (_) {
      ok = false;
    }
    area.remove();
    if (button) button.focus({ preventScroll: true });
    return ok;
  }

  function say(region, words) {
    if (!region || !words) return;
    region.textContent = '';
    // A fresh text node a moment later, so the same words are read again on a second copy.
    root.setTimeout(() => { region.textContent = words; }, 60);
  }

  function bindCopies(scope) {
    for (const button of all(scope, '[data-pfe-copy]')) {
      if (button.hasAttribute('data-pfe-bound')) continue;
      button.setAttribute('data-pfe-bound', '');
      const widget = button.closest('[data-pfe-copy-widget]');
      const region = widget && widget.querySelector('[data-pfe-said]');
      button.hidden = false;
      button.addEventListener('click', async () => {
        const ok = await copyText(button.getAttribute('data-pfe-copy') || '', button);
        if (ok && region) say(region, region.getAttribute('data-pfe-copied'));
      });
    }
  }

  /* ---------- Hero ripple: three waves once it is fully on screen ---------- */
  function wireRipple(scope) {
    if (reduce || !hasIO) return;
    for (const ripple of all(scope, '.pfe-ripple')) {
      if (ripple.hasAttribute('data-pfe-bound')) continue;
      ripple.setAttribute('data-pfe-bound', '');
      const seen = new root.IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.97)) return;
        seen.disconnect();
        ripple.classList.add('is-live');
      }, { threshold: [0.98] });
      seen.observe(ripple);
    }
  }

  /* ---------- Sticky bar ---------- */
  // Rebuilt page-wide on every run: the hero and the bar live in one section, a blocker (the gift) in the other.
  let barWatch = [];
  function wireBar() {
    for (const observer of barWatch) observer.disconnect();
    barWatch = [];
    const bar = doc.querySelector('[data-pfe-bar]');
    const heroCta = doc.querySelector('[data-pfe-hero-cta]');
    if (!bar || !heroCta || !hasIO) return;
    // Unknown until the hero observer's first report; the bar shows only once the button is known to be off screen.
    let heroOff = null;
    let heroSeen = false;
    let blockSeen = false;
    const covering = new Set();
    // The first reports set the state at load as it is, without the slide-in: nothing on the first screen animates in.
    const sync = () => {
      const show = heroOff === true && covering.size === 0;
      if (bar.hidden === !show) return;
      bar.hidden = !show;
      bar.classList.remove('is-in');
      if (show && heroSeen && blockSeen && !reduce) {
        void bar.offsetWidth;
        bar.classList.add('is-in');
      }
    };
    // The button counts as on screen only while most of it shows: a sliver at the screen's edge is no button to tap, so
    // the bar shows there (and covers the sliver).
    const hero = new root.IntersectionObserver((entries) => {
      for (const entry of entries) heroOff = !entry.isIntersecting || entry.intersectionRatio < 0.6;
      sync();
      heroSeen = true;
    }, { threshold: [0, 0.6, 1] });
    hero.observe(heroCta);
    const blockers = new root.IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) covering.add(entry.target);
        else covering.delete(entry.target);
      }
      sync();
      blockSeen = true;
    });
    const nodes = doc.querySelectorAll('[data-pfe-bar-hide]');
    for (const node of nodes) blockers.observe(node);
    if (!nodes.length) blockSeen = true;
    barWatch = [hero, blockers];
  }

  /* ---------- The gift ---------- */
  // What stops each gift's watcher or scratch card, for the editor's unload.
  const giftStop = new WeakMap();
  function wireGift(scope) {
    for (const gift of all(scope, '[data-pfe-gift]')) {
      if (gift.hasAttribute('data-pfe-bound') || gift.hasAttribute('data-pfe-state')) continue;
      gift.setAttribute('data-pfe-bound', '');
      if (hasIO) {
        const near = new root.IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          near.disconnect();
          giftStop.delete(gift);
          buildScratch(gift);
        }, { rootMargin: '400px 0px' });
        near.observe(gift);
        giftStop.set(gift, () => near.disconnect());
      } else {
        buildScratch(gift);
      }
    }
  }

  // The offer line follows PocketEmailOffer.available() from the reveal on, as the store's other offer surfaces do
  // (pocket-cro.js, sections/pf-pricing.liquid): asked again when the offer's dialog closes (a signup, or the store
  // learning this shopper has ordered), on a back/forward-cache return, and when a tap on it opens nothing.
  function showOffer(node) {
    const box = node.querySelector('[data-pfe-offer]');
    if (!box) return;
    if (!root.PocketEmailOffer) {
      // The offer's script comes later in the page: ask again once everything has loaded.
      if (doc.readyState !== 'complete') root.addEventListener('load', () => showOffer(node), { once: true });
      return;
    }
    const watch = new AbortController();
    const paint = () => {
      // Removed with its section (the theme editor): stop listening.
      if (!node.isConnected) { watch.abort(); return; }
      let on = false;
      try { on = typeof root.PocketEmailOffer?.available === 'function' && root.PocketEmailOffer.available() === true; } catch (_) { on = false; }
      box.hidden = !on;
    };
    paint();
    const button = box.querySelector('[data-pfe-offer-open]');
    if (button && !button.hasAttribute('data-pfe-bound')) {
      button.setAttribute('data-pfe-bound', '');
      button.addEventListener('click', () => {
        let opened = false;
        try { opened = root.PocketEmailOffer.open('eli5_gift') !== false; } catch (_) { /* the offer's own script owns its errors */ }
        if (!opened) paint();
      });
    }
    if (node.hasAttribute('data-pfe-offer-watch')) return;
    node.setAttribute('data-pfe-offer-watch', '');
    const on = { signal: watch.signal };
    const dialog = doc.getElementById('PFEmailOffer');
    if (dialog) dialog.addEventListener('close', paint, on);
    root.addEventListener('pageshow', (event) => { if (event.persisted) paint(); }, on);
  }

  function buildScratch(node) {
    if (node.hasAttribute('data-pfe-state')) return;
    const card = node.querySelector('[data-pfe-card]');
    const face = node.querySelector('[data-pfe-face]');
    const cover = node.querySelector('[data-pfe-cover]');
    const reveal = node.querySelector('[data-pfe-reveal]');
    const title = node.querySelector('[data-pfe-face-title]');
    const how = node.querySelector('[data-pfe-how]');
    const shown = () => {
      node.setAttribute('data-pfe-state', 'revealed');
      showOffer(node);
    };
    if (!card || !face || reduce) { shown(); return; }
    // Focus (a links-list or rotor jump) is already on the face: never make it inert under the user; it stays shown.
    if (face.contains(doc.activeElement)) { shown(); return; }

    const canvas = doc.createElement('canvas');
    let ctx = null;
    // Read back every few moves to measure the cleared share: a CPU-backed canvas makes each read a plain copy.
    try { ctx = typeof canvas.getContext === 'function' ? canvas.getContext('2d', { willReadFrequently: true }) : null; } catch (_) { ctx = null; }
    if (!ctx) { shown(); return; }

    node.setAttribute('data-pfe-state', 'covered');
    canvas.className = 'pfe-gift__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    face.after(canvas);
    face.setAttribute('inert', '');
    if (cover) {
      cover.hidden = false;
      canvas.after(cover);
    }
    if (reveal) reveal.hidden = false;
    // The how-to line stays once shown: hiding it at the reveal would move the card under the reader's finger.
    if (how) how.hidden = false;

    let width = 0;
    let height = 0;
    let ratio = 1;
    function paint() {
      const box = card.getBoundingClientRect();
      width = Math.max(1, Math.round(box.width));
      height = Math.max(1, Math.round(box.height));
      ratio = Math.min(root.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      // The back of a Pocket Factory card: ink, with the tap's lime rings off to one side.
      ctx.fillStyle = '#15170f';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = 'rgba(214, 247, 83, 0.22)';
      ctx.lineWidth = 2;
      const cx = width * 0.86;
      const cy = height * 0.5;
      for (let r = 18; r < Math.max(width, height); r += 22) {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.strokeStyle = '#d6f753';
      ctx.lineWidth = 2;
      ctx.strokeRect(5, 5, width - 10, height - 10);
    }
    paint();

    let start = null;
    let last = null;
    let scratching = false;
    let moves = 0;
    let done = false;

    const point = (event) => {
      const box = canvas.getBoundingClientRect();
      return { x: event.clientX - box.left, y: event.clientY - box.top };
    };

    function cleared() {
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        const step = 4 * 6;
        let clear = 0;
        let all = 0;
        for (let i = 3; i < data.length; i += step) {
          all += 1;
          if (data[i] < 128) clear += 1;
        }
        return all ? clear / all : 0;
      } catch (_) {
        return 0;
      }
    }

    function scratch(to) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 40;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      last = to;
      moves += 1;
      if (moves % 8 === 0 && cleared() >= 0.4) finish();
    }

    const lifecycle = new AbortController();
    const on = { signal: lifecycle.signal };
    canvas.addEventListener('pointerdown', (event) => {
      if (done || (event.pointerType === 'mouse' && event.button !== 0)) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      last = point(event);
      scratching = false;
    }, on);
    canvas.addEventListener('pointermove', (event) => {
      if (!start || event.pointerId !== start.id || done) return;
      // A mouse press released off the card never reached pointerup here: a move with no button held forgets it.
      if (event.pointerType === 'mouse' && (event.buttons & 1) === 0) { end(); return; }
      if (!scratching) {
        const dx = Math.abs(event.clientX - start.x);
        const dy = Math.abs(event.clientY - start.y);
        if (dx < 6 && dy < 6) return;
        // Up or down first: that is a scroll, the page's own (pan-y); this drag never scratches.
        if (dy >= dx) { start = null; return; }
        scratching = true;
        canvas.classList.add('is-scratching');
        if (cover) cover.classList.add('is-faded');
        try { canvas.setPointerCapture(event.pointerId); } catch (_) { /* capture is a nicety */ }
      }
      event.preventDefault();
      scratch(point(event));
    }, on);
    function end() {
      if (scratching && !done && cleared() >= 0.4) finish();
      start = null;
      scratching = false;
      canvas.classList.remove('is-scratching');
    }
    canvas.addEventListener('pointerup', end, on);
    canvas.addEventListener('pointercancel', end, on);
    canvas.addEventListener('lostpointercapture', end, on);

    let lastWidth = card.getBoundingClientRect().width;
    root.addEventListener('resize', () => {
      if (done) return;
      // Removed with its section (the theme editor): let go of the listeners and the canvas.
      if (!node.isConnected) { lifecycle.abort(); return; }
      const now = card.getBoundingClientRect().width;
      if (Math.abs(now - lastWidth) < 1) return;
      lastWidth = now;
      paint();
    }, { passive: true, signal: lifecycle.signal });

    function finish() {
      if (done) return;
      done = true;
      lifecycle.abort();
      const hadFocus = reveal && doc.activeElement === reveal;
      face.removeAttribute('inert');
      card.classList.add('is-revealing');
      if (cover) cover.classList.add('is-faded');
      root.setTimeout(() => {
        canvas.remove();
        if (cover) cover.hidden = true;
        card.classList.remove('is-revealing');
      }, 360);
      if (reveal) reveal.hidden = true;
      // Pressed with the keyboard: the button goes, so its focus moves to what it revealed. A scratch never moves focus.
      if (hadFocus && title) title.focus({ preventScroll: true });
      shown();
    }

    if (reveal) reveal.addEventListener('click', finish, { once: true, signal: lifecycle.signal });
    giftStop.set(node, () => lifecycle.abort());
  }

  /* ---------- Run, and again for the theme editor's section re-renders ---------- */
  function setup(scope) {
    bindCopies(scope);
    wireRipple(scope);
    wireGift(scope);
  }
  setup(doc);
  wireBar();

  const PARTS = '[data-pfe-copy], .pfe-ripple, [data-pfe-bar], [data-pfe-hero-cta], [data-pfe-bar-hide], [data-pfe-gift]';
  const ours = (target) => !!target && typeof target.querySelector === 'function' && (target.matches(PARTS) || !!target.querySelector(PARTS));
  doc.addEventListener('shopify:section:load', (event) => {
    if (!ours(event.target)) return;
    setup(event.target);
    wireBar();
  });
  doc.addEventListener('shopify:section:unload', (event) => {
    if (!ours(event.target)) return;
    for (const gift of all(event.target, '[data-pfe-gift]')) {
      const stop = giftStop.get(gift);
      if (stop) stop();
      giftStop.delete(gift);
    }
    // Once the old section has gone, the bar watches what is left (a load that follows rebuilds it again).
    root.setTimeout(wireBar, 0);
  });
})(typeof window !== 'undefined' ? window : globalThis);
